import {
  useMutation, useQuery, useQueryClient, keepPreviousData,
} from "@tanstack/react-query";

import { api } from "@/lib/api";
import type {
  IndicatorDetail, IndicatorDirection, IndicatorPeriod, IndicatorRow, IndicatorValueRow,
} from "@/lib/types";
import type { Visibility } from "@/hooks/useDocumentMutations";

interface Paginated<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

/** GET /api/v1/indicators/ — visibility-scoped list. */
export function useIndicators(opts: { search?: string; page?: number; category?: string } = {}) {
  return useQuery<Paginated<IndicatorRow>>({
    queryKey: ["indicators", "list", opts],
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const params = new URLSearchParams();
      if (opts.search)   params.set("search",   opts.search);
      if (opts.category) params.set("category", opts.category);
      if (opts.page)     params.set("page",     String(opts.page));
      const { data } = await api.get<Paginated<IndicatorRow>>(
        `/indicators/?${params.toString()}`,
      );
      return data;
    },
  });
}

export interface CategoryRow { id: string; name: string; code: string }

/** GET /api/v1/categories/ — shared between Document/KPI/Indicator.
 *  The endpoint is sometimes paged (DRF default) and sometimes a bare
 *  list (no pagination class on the viewset); normalise both into a
 *  plain array via the `select` callback so consumers can just .map(). */
export function useCategories() {
  return useQuery<CategoryRow[]>({
    queryKey: ["categories", "list"],
    queryFn: async () => {
      const { data } = await api.get<CategoryRow[] | { results: CategoryRow[] }>(
        "/categories/",
      );
      return Array.isArray(data) ? data : (data?.results ?? []);
    },
    staleTime: 5 * 60_000,
  });
}

export function useCreateCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (name: string) => {
      const { data } = await api.post<CategoryRow>("/categories/", { name });
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["categories"] }),
  });
}

export function useUpdateCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, name }: { id: string; name: string }) => {
      const { data } = await api.patch<CategoryRow>(`/categories/${id}/`, { name });
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["categories"] }),
  });
}

export function useDeleteCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => { await api.delete(`/categories/${id}/`); },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["categories"] }),
  });
}

/** GET /api/v1/indicators/<id>/ — detail incl. recent values. */
export function useIndicator(id: string | undefined) {
  return useQuery<IndicatorDetail>({
    enabled: !!id,
    queryKey: ["indicators", "detail", id],
    queryFn: async () => {
      const { data } = await api.get<IndicatorDetail>(`/indicators/${id}/`);
      return data;
    },
  });
}

/** GET /api/v1/indicator-values/?indicator=<id> — full timeseries. */
export function useIndicatorValues(indicatorId: string | undefined) {
  return useQuery<Paginated<IndicatorValueRow>>({
    enabled: !!indicatorId,
    queryKey: ["indicator-values", indicatorId],
    queryFn: async () => {
      const { data } = await api.get<Paginated<IndicatorValueRow>>(
        `/indicator-values/?indicator=${indicatorId}`,
      );
      return data;
    },
  });
}

// ---------------------------------------------------------------------------
// Mutations — all CEO+ gated on the backend.
// ---------------------------------------------------------------------------

export interface IndicatorInput {
  name: string;
  unit?: string;
  description?: string;
  direction: IndicatorDirection;
  period: IndicatorPeriod;
  /** One or more Category ids — at least one required by the API. */
  categories: string[];
  visibility: Visibility;
}

export function useCreateIndicator() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: IndicatorInput) => {
      const { data } = await api.post<IndicatorDetail>("/indicators/", input);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["indicators"] });
    },
  });
}

export function useUpdateIndicator() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      { id, input }: { id: string; input: Partial<IndicatorInput> },
    ) => {
      const { data } = await api.patch<IndicatorDetail>(`/indicators/${id}/`, input);
      return data;
    },
    onSuccess: (_d, { id }) => {
      qc.invalidateQueries({ queryKey: ["indicators"] });
      qc.invalidateQueries({ queryKey: ["indicators", "detail", id] });
    },
  });
}

export function useDeleteIndicator() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => { await api.delete(`/indicators/${id}/`); },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["indicators"] }),
  });
}

export interface IndicatorValueInput {
  indicator: string;
  date: string;            // YYYY-MM-DD
  value: number | string;
  target?: number | string | null;
  note?: string;
}

export function useCreateIndicatorValue() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: IndicatorValueInput) => {
      const { data } = await api.post<IndicatorValueRow>("/indicator-values/", input);
      return data;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["indicator-values", vars.indicator] });
      qc.invalidateQueries({ queryKey: ["indicators", "detail", vars.indicator] });
      qc.invalidateQueries({ queryKey: ["indicators"] });
    },
  });
}

export function useUpdateIndicatorValue() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      { id, input }: { id: string; input: Partial<IndicatorValueInput> },
    ) => {
      const { data } = await api.patch<IndicatorValueRow>(`/indicator-values/${id}/`, input);
      return data;
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["indicator-values", data.indicator] });
      qc.invalidateQueries({ queryKey: ["indicators", "detail", data.indicator] });
    },
  });
}

export function useDeleteIndicatorValue() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, indicator }: { id: string; indicator: string }) => {
      await api.delete(`/indicator-values/${id}/`);
      return { id, indicator };
    },
    onSuccess: ({ indicator }) => {
      qc.invalidateQueries({ queryKey: ["indicator-values", indicator] });
      qc.invalidateQueries({ queryKey: ["indicators", "detail", indicator] });
      qc.invalidateQueries({ queryKey: ["indicators"] });
    },
  });
}
